import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAgentRealtime } from "@/hooks/useAgentRealtime";
import { toast } from "sonner";
import {
  LayoutDashboard, Users2, ContactRound, MessageSquareText,
  BellRing, LogOut, Sparkles, PanelLeft, X, CalendarClock, Bot, ShieldCheck, Wallet, Receipt, TrendingUp, Target, Trophy, BrainCircuit, SlidersHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/leads", icon: ContactRound, label: "Leads" },
  { to: "/admin/lead-settings", icon: SlidersHorizontal, label: "Lead Settings" },
  { to: "/admin/followups", icon: CalendarClock, label: "Follow-ups" },
  { to: "/admin/orders", icon: Wallet, label: "Orders" },
  { to: "/admin/expenses", icon: Receipt, label: "Expenses" },
  { to: "/admin/pnl", icon: TrendingUp, label: "P&L Report" },
  { to: "/admin/targets", icon: Target, label: "Targets" },
  { to: "/admin/leaderboard", icon: Trophy, label: "Leaderboard" },
  { to: "/admin/team", icon: Users2, label: "Team" },
  { to: "/admin/activity", icon: ShieldCheck, label: "Staff Activity" },
  { to: "/admin/templates", icon: MessageSquareText, label: "Templates" },
  { to: "/admin/ai", icon: BrainCircuit, label: "AI Manager" },
  { to: "/admin/notifications", icon: BellRing, label: "Notifications" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);
  // Admin realtime: refresh lead lists + toast new-lead alerts.
  useAgentRealtime({
    enabled: !!user?.id,
    onEvent: (msg) => {
      if (msg.type === "lead_created" && msg.lead) {
        toast(`New lead: ${msg.lead.name}`, { description: msg.lead.phone });
      }
    },
  });
  useEffect(() => {
    const onForce = () => {
      toast.error("You have been signed out from another device.");
      logout().then(() => nav("/login", { replace: true }));
    };
    window.addEventListener("crm:force-logout", onForce);
    return () => window.removeEventListener("crm:force-logout", onForce);
  }, [logout, nav]);

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="grain flex min-h-screen bg-background">
      {/* Sidebar (desktop) */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border bg-card/50 lg:flex">
        <div className="flex shrink-0 items-center gap-2.5 border-b border-border px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-base font-bold leading-none">Northline</div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Admin console</div>
          </div>
        </div>
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium tap-lift ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border p-3">
          <div className="mb-3 rounded-md border border-border bg-background/60 p-3 text-xs">
            <div className="mono uppercase tracking-widest text-[10px] text-muted-foreground">Signed in</div>
            <div className="mt-1 truncate font-semibold">{user?.name}</div>
            <div className="truncate text-muted-foreground">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="w-full justify-start rounded-full"
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur">
          <button
            className="rounded-md border border-border p-1.5 lg:hidden"
            onClick={() => setOpenMobile(true)}
            data-testid="mobile-menu-open-btn"
            aria-label="Open menu"
          >
            <PanelLeft className="h-4 w-4" />
          </button>
          <div className="font-display text-sm font-semibold">Admin</div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell role="admin" />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer */}
      {openMobile && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpenMobile(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-72 flex-col border-r border-border bg-card">
            <div className="flex items-center justify-between border-b border-border p-4">
              <div className="font-display font-bold">Menu</div>
              <button onClick={() => setOpenMobile(false)} className="rounded-md border border-border p-1.5">
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 p-3">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpenMobile(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium ${
                      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" /> {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-border p-3">
              <Button variant="outline" onClick={onLogout} className="w-full rounded-full">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </Button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
